import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { DailyDataPoint } from '../hooks/useAnalytics';

interface AnalyticsChartProps {
  data: DailyDataPoint[];
  height?: number;
}

export function AnalyticsChart({ data, height = 300 }: AnalyticsChartProps) {
  const threshold = 0.7; // Hard-coded to 70%
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    const margin = { top: 20, right: 30, bottom: 50, left: 60 };
    const width = svgRef.current.parentElement?.clientWidth || 800;
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse dates and prepare data
    const parseDate = d3.timeParse('%Y-%m-%d');
    const chartData = data.map(d => ({
      ...d,
      parsedDate: parseDate(d.date) as Date
    }));

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(chartData, d => d.parsedDate) as [Date, Date])
      .range([0, chartWidth]);

    const y = d3.scaleLinear()
      .domain([0, Math.max(1, d3.max(chartData, d => d.ratio) || 1)])
      .nice()
      .range([chartHeight, 0]);

    // Colors
    const primaryColor = '#3b82f6';
    const thresholdColor = '#f97316';
    const gradientColor = '#60a5fa';

    // Gradient for area
    const gradient = svg.append('defs')
      .append('linearGradient')
      .attr('id', 'analyticsGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', gradientColor)
      .attr('stop-opacity', 0.4);

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', gradientColor)
      .attr('stop-opacity', 0.05);

    // Multiple threshold lines
    const thresholds = [
      { value: 1.0, color: '#22c55e', label: '100%', opacity: 0.6, dash: '2,2' },
      { value: 0.75, color: '#84cc16', label: '75%', opacity: 0.5, dash: '4,2' },
      { value: threshold, color: thresholdColor, label: `${(threshold * 100).toFixed(0)}%`, opacity: 0.8, dash: '8,4' },
      { value: 0.5, color: '#f97316', label: '50%', opacity: 0.7, dash: '6,2' },
      { value: 0.1, color: '#ef4444', label: '10%', opacity: 0.6, dash: '4,4' }
    ];

    thresholds.forEach(({ value, color, label, opacity, dash }) => {
      // Threshold line
      svg.append('line')
        .attr('x1', 0)
        .attr('x2', chartWidth)
        .attr('y1', y(value))
        .attr('y2', y(value))
        .attr('stroke', color)
        .attr('stroke-width', value === threshold ? 2 : 1)
        .attr('stroke-dasharray', dash)
        .attr('opacity', opacity);

      // Threshold label
      svg.append('text')
        .attr('x', chartWidth - 5)
        .attr('y', y(value) - 8)
        .attr('text-anchor', 'end')
        .attr('font-size', value === threshold ? '12px' : '10px')
        .attr('fill', color)
        .attr('font-weight', value === threshold ? '500' : '400')
        .text(label);
    });

    // 100% reference line
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', y(1))
      .attr('y2', y(1))
      .attr('stroke', '#22c55e')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4')
      .attr('opacity', 0.6);

    // Area generator
    const area = d3.area<typeof chartData[0]>()
      .x(d => x(d.parsedDate))
      .y0(chartHeight)
      .y1(d => y(d.ratio))
      .curve(d3.curveMonotoneX);

    // Draw area
    svg.append('path')
      .datum(chartData)
      .attr('fill', 'url(#analyticsGradient)')
      .attr('d', area);

    // Line generator
    const line = d3.line<typeof chartData[0]>()
      .x(d => x(d.parsedDate))
      .y(d => y(d.ratio))
      .curve(d3.curveMonotoneX);

    // Draw line
    svg.append('path')
      .datum(chartData)
      .attr('fill', 'none')
      .attr('stroke', primaryColor)
      .attr('stroke-width', 2.5)
      .attr('d', line)
      .attr('stroke-linecap', 'round');

    // Add dots for data points
    svg.selectAll('.data-point')
      .data(chartData)
      .enter()
      .append('circle')
      .attr('class', 'data-point')
      .attr('cx', d => x(d.parsedDate))
      .attr('cy', d => y(d.ratio))
      .attr('r', 3)
      .attr('fill', d => d.ratio < threshold ? thresholdColor : primaryColor)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        // Tooltip
        const tooltip = d3.select('body')
          .append('div')
          .attr('class', 'analytics-tooltip')
          .style('position', 'absolute')
          .style('background', 'rgba(15, 23, 42, 0.95)')
          .style('border', '1px solid rgba(148, 163, 184, 0.2)')
          .style('border-radius', '8px')
          .style('padding', '12px')
          .style('font-size', '14px')
          .style('color', '#f1f5f9')
          .style('pointer-events', 'none')
          .style('z-index', '1000')
          .style('box-shadow', '0 8px 32px rgba(0, 0, 0, 0.3)');

        const performance = d.ratio * 100;
        const verdict = performance < 20 ? '💀 Disastrous' :
                       performance < 50 ? '❌ Terrible' :
                       performance < 75 ? '⚠️ Poor' :
                       performance < 90 ? '😐 Mediocre' : '✅ Decent';

        tooltip.html(`
          <div style="font-weight: 600; margin-bottom: 8px;">${d.date}</div>
          <div style="display: flex; gap: 16px;">
            <div>
              <div style="color: #60a5fa; font-weight: 500;">Performance</div>
              <div style="font-size: 18px; font-weight: 700;">${performance.toFixed(1)}%</div>
              <div style="font-size: 12px; color: #94a3b8;">${verdict}</div>
            </div>
            <div>
              <div style="color: #94a3b8;">Generation</div>
              <div>${d.actual_output_mwh.toFixed(1)} MWh</div>
            </div>
            <div>
              <div style="color: #94a3b8;">Cloud Cover</div>
              <div>${d.cloud_cover_avg.toFixed(1)}%</div>
            </div>
          </div>
        `);

        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');

        // Highlight point
        d3.select(this)
          .attr('r', 5)
          .attr('stroke-width', 2);
      })
      .on('mouseout', function() {
        // Remove tooltip
        d3.select('.analytics-tooltip').remove();

        // Reset point size
        d3.select(this)
          .attr('r', 3)
          .attr('stroke-width', 1.5);
      });

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(x)
        .ticks(Math.min(chartData.length, 8))
        .tickFormat(d => d3.timeFormat('%b %d')(d as Date))
      )
      .attr('class', 'chart-axis')
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .attr('fill', '#94a3b8')
      .attr('font-size', '11px');

    // Y axis
    svg.append('g')
      .call(d3.axisLeft(y)
        .ticks(6)
        .tickFormat(d => `${(Number(d) * 100).toFixed(0)}%`)
      )
      .attr('class', 'chart-axis')
      .selectAll('text')
      .attr('fill', '#94a3b8')
      .attr('font-size', '11px');

    // Y axis label
    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -45)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('fill', '#cbd5e1')
      .text('Performance Ratio');

    // X axis label
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('fill', '#cbd5e1')
      .text('Date');

  }, [data, threshold, height]);

  if (!data.length) {
    return (
      <div className="chart-placeholder">
        <div className="placeholder-content">
          <div className="placeholder-icon">📊</div>
          <div className="placeholder-text">No data available for selected period</div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <svg ref={svgRef} className="analytics-chart-svg" />
    </div>
  );
}
